import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getOwnApplication, portalApplicationFormApi, saveOwnApplication, submitOwnApplication } from '../../api/applicationForm';
import { ApplicationFormApiContext } from '../students/wizard/applicationFormApi';
import {
  buildStudentPayload,
  emptyWizardValues,
  isHealthDeclarationComplete,
  resultsToDrafts,
  studentToWizardValues,
  SubjectResultDraft,
  WizardValues,
} from '../students/wizard/wizardTypes';
import { Step1ApplicationDetails } from '../students/wizard/Step1ApplicationDetails';
import { Step2PersonalDetails } from '../students/wizard/Step2PersonalDetails';
import { Step3AcademicDetails } from '../students/wizard/Step3AcademicDetails';
import { Step4HigherEducation } from '../students/wizard/Step4HigherEducation';
import { Step5PreferredStudy } from '../students/wizard/Step5PreferredStudy';
import { Step6ParentsGuardian } from '../students/wizard/Step6ParentsGuardian';
import { Step7VisaHistory } from '../students/wizard/Step7VisaHistory';
import { Step8FinancialInformation } from '../students/wizard/Step8FinancialInformation';
import { Step10Declaration } from '../students/wizard/Step10Declaration';
import { Step11Review } from '../students/wizard/Step11Review';
import { PortalDocumentsTab } from './PortalDocumentsTab';
import { APPLICATION_FORM_STATUS_LABEL } from './applicationFormStatus';
import { PortalApplicationView } from './PortalApplicationView';
import { describeNumber, isTemporaryNumber } from '../../components/temporaryNumber';

/** The same steps as the counsellor's University Application Form. */
const STEPS = [
  'Application Details',
  'Personal Details',
  'Academic Details',
  'Higher Education',
  'Preferred Study',
  'Parents / Guardian',
  'Visa History',
  'Financial Information',
  'Documents',
  'Declaration',
  'Review',
];
const DECLARATION_STEP = STEPS.indexOf('Declaration');

const errorText = (err: unknown) =>
  isAxiosError(err) ? ((err.response?.data as { message?: string } | undefined)?.message ?? 'Something went wrong.') : 'Something went wrong.';

/** Student Portal → Application Form (2026-11-06): the student fills the
 * University Application Form their counsellor invited them to — the same
 * steps, fields and validation as the counsellor's form, saved as they go —
 * and submits it for review. Read-only while it is being reviewed or once
 * approved. */
export const PortalApplicationTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['portal-application'], queryFn: getOwnApplication });
  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState<WizardValues>(emptyWizardValues);
  const [olResults, setOlResults] = useState<SubjectResultDraft[]>([]);
  const [alResults, setAlResults] = useState<SubjectResultDraft[]>([]);
  const [hydratedFor, setHydratedFor] = useState<number | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const data = query.data;
  // Load the record once per student (not on every refetch, which would wipe unsaved typing).
  useEffect(() => {
    if (!data || hydratedFor === data.student.id) return;
    setValues(studentToWizardValues(data.student));
    setOlResults(resultsToDrafts(data.student.olResults));
    setAlResults(resultsToDrafts(data.student.alResults));
    setActiveStep(Math.min(Math.max(data.student.lastCompletedStep ?? 0, 0), STEPS.length - 1));
    setHydratedFor(data.student.id);
  }, [data, hydratedFor]);

  const save = useMutation({
    mutationFn: (lastCompletedStep: number) => {
      // Branch and campaign are IMC's to set — never sent from the portal.
      const payload: Record<string, unknown> = { ...buildStudentPayload(values, olResults, alResults), lastCompletedStep };
      delete payload.branchId;
      delete payload.campaignId;
      return saveOwnApplication(payload);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['portal-application'], result);
      setSavedAt(new Date());
    },
  });
  const submit = useMutation({
    mutationFn: async () => {
      await save.mutateAsync(STEPS.length - 1);
      return submitOwnApplication();
    },
    onSuccess: (result) => queryClient.setQueryData(['portal-application'], result),
  });

  // Wait for the saved answers before showing a step (no flash of step 1).
  if (query.isLoading || (query.data && hydratedFor !== query.data.student.id)) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (query.isError || !data) return <Alert severity="error">Couldn&apos;t load your application form. Please try again later.</Alert>;

  const { form, editable } = data;
  const setField = <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => setValues((prev) => ({ ...prev, [field]: value }));
  const isStep2Valid = !!(values.firstName.trim() && values.lastName.trim() && values.phone.trim());
  const isDeclarationValid = isHealthDeclarationComplete(values);
  const busy = save.isPending || submit.isPending;
  const isLastStep = activeStep === STEPS.length - 1;
  const next = async () => {
    await save.mutateAsync(activeStep + 1);
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const header = (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" fontWeight={700}>
          University Application Form
        </Typography>
        <Chip size="small" color={form.status === 'APPROVED' ? 'success' : form.status === 'REJECTED' ? 'error' : 'primary'} label={APPLICATION_FORM_STATUS_LABEL[form.status]} data-testid="application-form-status" />
        {form.applicationNumber && <Chip size="small" variant="outlined" color={isTemporaryNumber(form.applicationNumber) ? 'warning' : 'default'} label={describeNumber(form.applicationNumber, 'application')} />}
      </Stack>
      {form.status === 'REJECTED' && form.rejectionReason && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <strong>Changes required:</strong> {form.rejectionReason}. Correct your application below and submit it again.
        </Alert>
      )}
      {(form.status === 'SUBMITTED' || form.status === 'RESUBMITTED') && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          Your application was submitted{form.submittedAt ? ` on ${new Date(form.submittedAt).toLocaleDateString()}` : ''} and is waiting for your counsellor&apos;s review.
        </Alert>
      )}
      {form.status === 'APPROVED' && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          Your application has been approved{form.reviewedAt ? ` (${new Date(form.reviewedAt).toLocaleDateString()})` : ''}. To change your details now, use Change requests.
        </Alert>
      )}
      {editable && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Your answers are saved as you move between steps — you can stop and continue later. When everything is complete, submit it for your counsellor&apos;s review.
        </Typography>
      )}
    </Paper>
  );

  // Completed with the counsellor (option 1, 2026-11-07): the whole
  // application, read-only; changes go through Change requests.
  if (form.method !== 'STUDENT_PORTAL') {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Your application was completed with your counsellor. This is what IMC has on file — to change anything, use Change requests.
        </Alert>
        <PortalApplicationView student={data.student} />
      </Box>
    );
  }

  return (
    <ApplicationFormApiContext.Provider value={portalApplicationFormApi}>
      <Box>
        {header}
        {!editable ? (
          <PortalApplicationView student={data.student} />
        ) : (
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            {isMobile ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Step {activeStep + 1} of {STEPS.length} · {STEPS[activeStep]}
                </Typography>
                <LinearProgress variant="determinate" value={((activeStep + 1) / STEPS.length) * 100} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
              </Box>
            ) : (
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, '& .MuiStepLabel-label': { fontSize: 12 } }}>
                {STEPS.map((label, i) => (
                  <Step key={label}>
                    <StepLabel sx={{ cursor: 'pointer' }} onClick={() => !busy && i < activeStep && setActiveStep(i)}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            <Box sx={{ mb: 1 }}>
              {(save.error || submit.error) && <Alert severity="error">{errorText(save.error ?? submit.error)}</Alert>}
              {save.isPending && (
                <Typography variant="caption" color="text.secondary">
                  Saving…
                </Typography>
              )}
              {!save.isPending && savedAt && (
                <Typography variant="caption" color="text.secondary">
                  Saved {savedAt.toLocaleTimeString()}
                </Typography>
              )}
            </Box>

            {activeStep === 0 && <Step1ApplicationDetails values={values} setField={setField} studentId={data.student.id} applicationNumber={data.student.applicationNumber} />}
            {activeStep === 1 && <Step2PersonalDetails values={values} setField={setField} />}
            {activeStep === 2 && (
              <Step3AcademicDetails values={values} setField={setField} olResults={olResults} setOlResults={setOlResults} alResults={alResults} setAlResults={setAlResults} />
            )}
            {activeStep === 3 && <Step4HigherEducation studentId={data.student.id} />}
            {activeStep === 4 && <Step5PreferredStudy values={values} setField={setField} />}
            {activeStep === 5 && <Step6ParentsGuardian values={values} setField={setField} />}
            {activeStep === 6 && <Step7VisaHistory values={values} setField={setField} />}
            {activeStep === 7 && <Step8FinancialInformation values={values} setField={setField} />}
            {/* Documents: the portal's own checklist and uploads. */}
            {activeStep === 8 && <PortalDocumentsTab />}
            {activeStep === 9 && <Step10Declaration values={values} setField={setField} studentId={data.student.id} />}
            {activeStep === 10 && <Step11Review studentId={data.student.id} />}

            <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <Button onClick={() => save.mutate(activeStep)} disabled={busy || !isStep2Valid}>
                Save progress
              </Button>
              {activeStep > 0 && (
                <Button onClick={() => setActiveStep((s) => Math.max(s - 1, 0))} disabled={busy}>
                  Previous
                </Button>
              )}
              {!isLastStep ? (
                <Button
                  variant="contained"
                  onClick={() => void next()}
                  disabled={busy || (activeStep === 1 && !isStep2Valid) || (activeStep === DECLARATION_STEP && !isDeclarationValid)}
                >
                  Next
                </Button>
              ) : (
                <Button variant="contained" color="success" onClick={() => submit.mutate()} disabled={busy || !isStep2Valid}>
                  {submit.isPending ? 'Submitting…' : form.status === 'REJECTED' ? 'Resubmit for review' : 'Submit for review'}
                </Button>
              )}
            </Stack>
          </Paper>
        )}
      </Box>
    </ApplicationFormApiContext.Provider>
  );
};
